import dynamic from 'next/dynamic';
import React from 'react';
import { FdrTitleText } from '@hotelplan/supergraph-api';
import { FdrPageComponents } from 'components/domain/fdr-page-components';
import FdrPageComponentsWithCustomBenefits from 'components/domain/fdr-page-components/fdr-page-components-with-custom-benefits';
import FdrSimilarDestinationsRecommendation from 'components/domain/fdr-page-components/recommenders/geo/fdr-similar-destinations-recommender';
import { TFdrHotelProduct } from 'components/domain/fdr-product/fdr-product.types';
import GeoHighlightsSection from 'components/domain/geo/GeoHighlightsSection';
import { FdrPageComponentsFragment } from 'schemas/fragment/page-components/fdr-page-components.generated';
import FdrGeoBlogRecommender from './blog/fdr-geo-blog-recommender';
import FdrGeoBestTravelPeriod from './fdr-geo-best-travel-period';
import FdrGeoChildren from './fdr-geo-children';
import FdrGeoProducts from './fdr-geo-products';
import {
  TFdrThGeoModuleObject,
  TFdrThGeoModuleObjectWithChildren,
} from './fdr-geo.types';
import {
  getFdrGeoAdventureTravels,
  isGeoProductGalleryAvailable,
} from './fdr-geo.utils';
import FdrGeoInformationSectionLongText from './information-section/fdr-geo-information-section-long-text';
import FdrGeoProductGallery from './product-gallery/fdr-geo-product-gallery';

export const renderThemeGeoPages = (
  id: string,
  themes: TFdrThGeoModuleObject['themeGeoPages'],
  geoObjectName: string
) => {
  if (!themes?.themeGeoPages?.length) return null;

  const FdrGeoThemeGeoRecommender = dynamic(
    () => import('./fdr-geo-theme-geo-recommender')
  );

  return (
    <FdrGeoThemeGeoRecommender
      id={id}
      themeGeoPages={themes}
      key="themes"
      geoObject={geoObjectName}
    />
  );
};

export const renderArticles = (
  id: string,
  blogArticles: TFdrThGeoModuleObject['relatedBlogArticles'],
  title: string
) => {
  if (!blogArticles?.pagination.total) {
    return null;
  }

  return (
    <FdrGeoBlogRecommender
      id={id}
      key="articles"
      blogArticles={blogArticles}
      title={title}
    />
  );
};

export const renderProductGallery = (
  id: string,
  products: TFdrThGeoModuleObject['products'],
  total: number,
  geoObjectName: string
) => {
  if (!isGeoProductGalleryAvailable(products)) return null;

  return (
    <FdrGeoProductGallery
      id={id}
      products={products}
      total={total}
      key="media"
      geoObject={geoObjectName}
    />
  );
};

export const renderGeoChildren = (
  geoChildren: TFdrThGeoModuleObjectWithChildren['children'],
  geoObjectName: string
) => {
  if (!geoChildren?.page?.length) return null;

  return (
    <FdrGeoChildren
      geoChildren={geoChildren}
      geoName={geoObjectName}
      key="children"
    />
  );
};

export const renderHotels = (
  hotels: TFdrHotelProduct<
    TFdrThGeoModuleObject['hotels']['productsPage'][number]
  >[],
  extraTitle: string,
  isB2b: boolean,
  regularTitle: string,
  extraHotelsSectionHeaderTitle: string,
  regularHotelsSectionHeaderTitle: string
) => {
  if (!hotels?.length) return null;

  const regularHotels = hotels.filter(h => !h.flags?.extraOrdinary);
  const extraOrdinaryHotels = hotels.filter(h => h.flags?.extraOrdinary);

  const FdrGeoHotels = dynamic(
    () => import('components/domain/fdr-geo/fdr-geo-hotels')
  );

  return (
    <React.Fragment key="hotels">
      {extraOrdinaryHotels?.length ? (
        <FdrGeoHotels
          hotels={extraOrdinaryHotels}
          sectionTitle={extraTitle}
          sectionHeaderTitle={extraHotelsSectionHeaderTitle}
        />
      ) : null}
      {isB2b && regularHotels?.length ? (
        <FdrGeoHotels
          hotels={regularHotels}
          sectionTitle={regularTitle}
          sectionHeaderTitle={regularHotelsSectionHeaderTitle}
        />
      ) : null}
    </React.Fragment>
  );
};

export const renderProducts = (
  id: string,
  geoModules: TFdrThGeoModuleObject,
  title: string
) => {
  const adventureTravels = getFdrGeoAdventureTravels(geoModules);

  if (!adventureTravels?.length) return null;

  return (
    <FdrGeoProducts
      id={id}
      total={geoModules.products.pagination.total}
      geoProducts={adventureTravels}
      geoObject={title}
      key="products"
    />
  );
};

export const renderSiblings = (id: string): JSX.Element => {
  return <FdrSimilarDestinationsRecommendation id={id} key="siblings" />;
};

export const renderHighlightsSection = (title: string, text: string) => {
  if (!title || !text) return null;

  return <GeoHighlightsSection title={title} text={text} />;
};

export const renderBestTravelPeriod = (
  title?: string,
  text?: string,
  accordion?: FdrTitleText[]
) => {
  if (!title || !text) return null;

  return (
    <FdrGeoBestTravelPeriod title={title} text={text} accordion={accordion} />
  );
};

export const renderGeoLongText = (
  longTextBlocks: { title: string; text: string }[],
  title?: string
) => {
  return (
    <FdrGeoInformationSectionLongText
      longTextBlocks={longTextBlocks}
      title={title}
      key="long-text"
    />
  );
};

export const renderDynamicComponents = (
  components: FdrPageComponentsFragment['components']
) => {
  return <FdrPageComponents components={components} />;
};

export const renderGeoStaticComponents = (
  components: FdrPageComponentsFragment['components']
) => {
  return <FdrPageComponentsWithCustomBenefits components={components} />;
};
